'use client';

import { usePathname, useRouter } from '@/i18n/navigation';
import { apiFetch } from '@/lib/api';
import { useAppData } from '@/lib/AppDataProvider';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const locales = [
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'en-US', label: 'English', flag: '🇺🇸' },
  { code: 'es-ES', label: 'Español', flag: '🇪🇸' },
] as const;

type LocaleCode = (typeof locales)[number]['code'];

type LanguageSwitcherProps = {
  variant?: 'default' | 'dark';
};

function useOptionalAppData() {
  try {
    return useAppData();
  } catch {
    return { user: null };
  }
}

function useSegmentLocale(): LocaleCode {
  const params = useParams();
  const raw = params?.locale;
  if (typeof raw === 'string' && locales.some((l) => l.code === raw)) {
    return raw as LocaleCode;
  }
  return locales[0].code;
}

export default function LanguageSwitcher({ variant = 'default' }: LanguageSwitcherProps) {
  const locale = useSegmentLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useOptionalAppData();

  const current = locales.find((l) => l.code === locale) ?? locales[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = (code: LocaleCode) => {
    setOpen(false);
    document.cookie = `NEXT_LOCALE=${code};path=/;max-age=31536000;SameSite=Lax`;
    router.replace(pathname, { locale: code });

    // Persist to user profile if authenticated
    if (user) {
      void apiFetch('/api/auth/preferred-language', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: code }),
      });
    }
  };

  const isDark = variant === 'dark';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
          isDark
            ? 'text-white/80 hover:text-white hover:bg-white/10'
            : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--secondary)]'
        }`}
        aria-label="Switch language"
        aria-expanded={open}
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">{current.flag} {current.label}</span>
        <span className="sm:hidden">{current.flag}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--background)] shadow-lg"
          >
            {locales.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => handleSwitch(l.code)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  l.code === locale
                    ? 'bg-[var(--primary-light)] text-[var(--primary)] font-medium'
                    : 'text-[var(--foreground)] hover:bg-[var(--secondary)]'
                }`}
              >
                <span className="text-base">{l.flag}</span>
                <span className="flex-1 text-left">{l.label}</span>
                {l.code === locale && <Check className="h-4 w-4 text-[var(--primary)]" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
